import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PenLine, Clock, CheckCircle, User, DollarSign, FileText, ArrowLeft, AlertCircle
} from 'lucide-react';
import { apiRequest } from '../../services/api';
import { Card, CardContent } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { SignaturePad } from '../../components/new-components/SignaturePad/SignaturePad';
import { toast } from 'sonner';

interface PendingAgreement {
  id: string;
  investmentId: string;
  confirmationCode: string;
  investorName: string;
  investorEmail: string;
  amount: number;
  unitCount: number;
  unitClass: string;
  titleOption: string;
  paymentMethod: string;
  investmentStatus: string;
  signedAt: string;
  createdAt: string;
}

interface AgreementDetail {
  id: string;
  investmentId: string;
  confirmationCode: string;
  investorName: string;
  investorEmail: string;
  amount: number;
  unitCount: number;
  unitClass: string;
  titleOption: string;
  paymentMethod: string;
  investmentStatus: string;
  investedAt: string;
  signedAt: string;
  status: string;
  investorSnapshot: any;
  badActorCheck: any;
}

const AdminCounterSign: React.FC = () => {
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AgreementDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [signatureImage, setSignatureImage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadPending = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiRequest<any>('GET', '/hivefund/mgmt/pending-countersign');
      if (res.success) {
        setPending(res.data || []);
      }
    } catch (err) {
      toast.error('Failed to load pending agreements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPending(); }, [loadPending]);

  const selectAgreement = async (id: string) => {
    try {
      setLoadingDetail(true);
      setSignatureImage('');
      const res = await apiRequest<any>('GET', `/hivefund/mgmt/countersign?id=${id}`);
      if (res.success) {
        setSelected(res.data);
      } else {
        toast.error(res.error || 'Failed to load agreement details');
      }
    } catch (err) {
      toast.error('Failed to load agreement');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCounterSign = async () => {
    if (!selected || !signatureImage) return;

    try {
      setSubmitting(true);
      const res = await apiRequest<any>('POST', `/hivefund/mgmt/countersign`, {
        subscriptionId: selected.id,
        signatureImage,
      });
      if (res.success) {
        toast.success('Agreement counter-signed successfully! Fully executed copies sent to both parties.');
        setSelected(null);
        setSignatureImage('');
        loadPending();
      } else {
        toast.error(res.error || 'Counter-sign failed');
      }
    } catch (err) {
      toast.error('Failed to counter-sign agreement');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val);

  const formatDate = (d: string) =>
    d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const titleOptionLabel = (opt: string) => {
    const map: Record<string, string> = {
      individual: 'Individual', joint_tenants: 'Joint Tenants', tenants_in_common: 'Tenants in Common',
      community_property: 'Community Property', trust: 'Trust', corporation: 'Corporation',
      llc: 'LLC', partnership: 'Partnership', ira: 'IRA', other: 'Other',
    };
    return map[opt] || opt;
  };

  const paymentLabel = (m: string) => {
    const map: Record<string, string> = { wire: 'Wire Transfer', ach: 'ACH / Bank Transfer', crypto: 'Cryptocurrency', check: 'Check' };
    return map[m] || m;
  };

  // ── Detail / Signing View ──
  if (selected) {
    const snap = selected.investorSnapshot || {};
    return (
      <div className="space-y-6">
        <button
          onClick={() => { setSelected(null); setSignatureImage(''); }}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Pending Agreements
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Counter-Sign Agreement</h1>
            <p className="text-gray-500 mt-1">Page 18 — Acceptance of Subscription</p>
          </div>
          <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 px-3 py-1">
            <Clock className="w-3.5 h-3.5 mr-1.5" /> Pending Your Signature
          </Badge>
        </div>

        {/* Investor & Investment Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <User className="w-4 h-4" /> Investor Information
              </h3>
              <div className="space-y-3">
                <Row label="Name" value={selected.investorName} />
                <Row label="Email" value={selected.investorEmail} />
                <Row label="Title Holding" value={titleOptionLabel(selected.titleOption)} />
                {snap.address && <Row label="Address" value={`${snap.address}, ${snap.city}, ${snap.state} ${snap.zip}`} />}
                {snap.phone && <Row label="Phone" value={snap.phone} />}
                {snap.entityName && <Row label="Entity" value={snap.entityName} />}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Investment Details
              </h3>
              <div className="space-y-3">
                <Row label="Amount" value={formatCurrency(selected.amount)} highlight />
                <Row label="Units" value={`${selected.unitCount} ${selected.unitClass}`} />
                <Row label="Payment" value={paymentLabel(selected.paymentMethod)} />
                <Row label="Confirmation" value={selected.confirmationCode} mono />
                <Row label="Invested" value={formatDate(selected.investedAt)} />
                <Row label="Signed by Investor" value={formatDate(selected.signedAt)} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bad Actor Check Summary */}
        {selected.badActorCheck && (
          <Card>
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Bad Actor Certification
              </h3>
              <div className="flex items-center gap-2">
                {selected.badActorCheck.allClear ? (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> All Clear
                  </Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-700 border-red-200">
                    <AlertCircle className="w-3.5 h-3.5 mr-1" /> Requires Review
                  </Badge>
                )}
                <span className="text-sm text-gray-500">
                  Investor certifies no disqualifying events under Rule 506(d)
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Acceptance Section — Page 18 */}
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="p-6 space-y-6">
            <div className="flex items-start gap-3">
              <div className="bg-amber-100 rounded-full p-2">
                <PenLine className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Acceptance of Subscription</h3>
                <p className="text-sm text-gray-600 mt-1">
                  By signing below, you accept this subscription on behalf of Honeyshares Management LLC
                  as Manager of The Hive Fund LLC, confirming review of the subscriber's qualifications
                  and suitability for investment.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-amber-200 p-4">
              <p className="text-sm text-gray-500 mb-1">Signing as:</p>
              <p className="text-base font-semibold text-gray-900">
                Keaton Thames, Manager — Honeyshares Management LLC
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Manager Signature</p>
              <SignaturePad
                onSignatureCapture={(dataUrl) => setSignatureImage(dataUrl)}
                label="Draw Your Signature"
                penColor="#1e3a5f"
              />
            </div>

            <div className="flex items-center gap-4 pt-2">
              <Button
                onClick={handleCounterSign}
                disabled={!signatureImage || submitting}
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-2.5"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <PenLine className="w-4 h-4" />
                    Counter-Sign & Accept Subscription
                  </span>
                )}
              </Button>
              <button
                onClick={() => { setSelected(null); setSignatureImage(''); }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Counter-Signatures</h1>
          <p className="text-gray-500 mt-1">
            Subscription agreements awaiting your acceptance signature (Page 18)
          </p>
        </div>
        <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 px-3 py-1.5 text-sm">
          {pending.length} Pending
        </Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
        </div>
      ) : pending.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700">All Caught Up</h3>
            <p className="text-gray-500 mt-1">No subscription agreements are pending your counter-signature.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pending.map((item) => (
            <Card
              key={item.id}
              className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-amber-400"
              onClick={() => selectAgreement(item.id)}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-amber-100 rounded-full p-2.5">
                      <PenLine className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{item.investorName}</h3>
                      <p className="text-sm text-gray-500">{item.investorEmail}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(item.amount)}</p>
                      <p className="text-xs text-gray-500">{item.unitCount} {item.unitClass}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Signed</p>
                      <p className="text-sm text-gray-700">{formatDate(item.signedAt)}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                      <Clock className="w-3 h-3 mr-1" /> Awaiting Signature
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

/** Simple two-column detail row */
function Row({ label, value, highlight, mono }: { label: string; value: string; highlight?: boolean; mono?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm font-medium ${highlight ? 'text-lg font-bold text-blue-900' : 'text-gray-900'} ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </span>
    </div>
  );
}

export default AdminCounterSign;
